import { notifyBrowserAuthUnauthorized } from './browserAuthTransport';
import type { PromptSuggestionCatalog } from '../types/promptSuggestions';
import { API_BASE_URL } from '../utils/config';

const PROMPT_SUGGESTIONS_PATH = `${API_BASE_URL}/prompt-suggestions`;

export interface PromptSuggestionsFetchResult {
  catalog: PromptSuggestionCatalog | null;
  etag: string | null;
  notModified: boolean;
}

export class PromptSuggestionsRequestError extends Error {
  readonly status: number;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = 'PromptSuggestionsRequestError';
    this.status = status;
  }
}

const buildRequestPath = (sessionId: string, locale: string): string => {
  const searchParameters = new URLSearchParams({
    session_id: sessionId,
    locale,
  });
  return `${PROMPT_SUGGESTIONS_PATH}?${searchParameters.toString()}`;
};

const readErrorMessage = async (response: Response): Promise<string> => {
  try {
    const body = await response.json();
    return typeof body?.detail === 'string'
      ? body.detail
      : 'Unable to load Coach suggestions.';
  } catch {
    return 'Unable to load Coach suggestions.';
  }
};

export const promptSuggestionsService = {
  async getSuggestions(
    sessionId: string,
    locale: string,
    options: { etag?: string; signal?: AbortSignal } = {},
  ): Promise<PromptSuggestionsFetchResult> {
    const response = await fetch(buildRequestPath(sessionId, locale), {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(options.etag ? { 'If-None-Match': options.etag } : {}),
      },
      signal: options.signal,
    });

    if (response.status === 401) {
      notifyBrowserAuthUnauthorized();
    }

    if (response.status === 304) {
      return {
        catalog: null,
        etag: response.headers.get('ETag'),
        notModified: true,
      };
    }

    if (!response.ok) {
      throw new PromptSuggestionsRequestError(
        response.status,
        await readErrorMessage(response),
      );
    }

    return {
      catalog: await response.json() as PromptSuggestionCatalog,
      etag: response.headers.get('ETag'),
      notModified: false,
    };
  },
};